'use client';

import { useQueryClient } from '@tanstack/react-query';
import { LogOut, Settings, UserCircle } from 'lucide-react';
import { logout as logoutApi } from '@/lib/api';
import { clearAuth } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import Avatar from '@/components/ui/Avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { DashboardRole } from '@/components/dashboard/Sidebar';

const ROLE_PROFILE_ROUTES: Record<DashboardRole, string | null> = {
  seafarer: '/seafarer/profile',
  shore: '/shore/profile',
  employer: '/employer/profile',
  institute: null,
  admin: null,
};

interface UserMenuProps {
  role: DashboardRole;
  userName?: string;
  userEmail?: string;
  className?: string;
}

export default function UserMenu({
  role,
  userName = 'User',
  userEmail,
  className,
}: UserMenuProps) {
  const queryClient = useQueryClient();
  const profileRoute = ROLE_PROFILE_ROUTES[role];

  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch {
      // Server errors are fine — proceed with local cleanup
    }

    clearAuth();
    queryClient.clear();
    window.location.href = '/login';
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Open user menu"
          className={cn(
            'flex items-center gap-2 rounded-full border border-border bg-background p-1.5 pr-3 text-sm font-semibold shadow-sm transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            className
          )}
        >
          <Avatar name={userName} size="sm" />
          <span className="hidden sm:inline">{userName}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-60 border border-neon-cyan/10 bg-panel/80 p-2 text-popover-foreground shadow-lg backdrop-blur-md"
      >
        <div className="flex items-center gap-3 border-b border-border p-3">
          <Avatar name={userName} size="md" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{userName}</p>
            {userEmail && (
              <p className="truncate text-xs text-muted-foreground">{userEmail}</p>
            )}
          </div>
        </div>

        <nav className="flex flex-col p-1">
          {profileRoute && (
            <a
              href={profileRoute}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground transition hover:bg-panel-hover"
            >
              <UserCircle className="h-4 w-4 text-muted-foreground" />
              Profile
            </a>
          )}
          <a
            href={`/${role}/settings`}
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground transition hover:bg-panel-hover"
          >
            <Settings className="h-4 w-4 text-muted-foreground" />
            Settings
          </a>
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-danger transition hover:bg-danger-light"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </nav>
      </PopoverContent>
    </Popover>
  );
}
